import React, { type FC, type ReactNode, memo, useCallback, useId, useState } from "react";
import { cn } from "@heroui/react";
import { ChevronRight } from "@gravity-ui/icons";

import { AGENT_PHASES } from "./agentPhases";
import { AgentStateIcon } from "./AgentStateIcon";
import { reportLineText } from "./reportText";
import type { AgentStepId, AgentVisualState, ReportLine } from "./runner/types";
import type { MessageId } from "@domain/intl/useTranslation";
import { useTranslation } from "@domain/intl/useTranslation";
import { ProductColors } from "@ui-components/ProductColors";

export interface AgentStepState {
    state: AgentVisualState;
    result?: ReportLine;
}

export type AgentStepStates = Record<AgentStepId, AgentStepState>;

const barColors: Record<AgentVisualState, string> = {
    done: "bg-white",
    nothing: "bg-white/65",
    skipped: "bg-white/65",
    failed: "bg-red-200",
    running: "bg-amber-200",
    pausing: "bg-amber-200",
    paused: "bg-amber-100",
    asking: "bg-amber-200",
    reading: "bg-amber-200",
    pending: "bg-white/30",
};

const waitWords: Partial<Record<AgentVisualState, MessageId>> = {
    running: "app.agent.stepList.running",
    pausing: "app.agent.stepList.pausing",
    paused: "app.agent.stepList.paused",
    asking: "app.agent.stepList.asking",
    pending: "app.agent.stepList.pending",
};

interface ListProps {
    steps: AgentStepId[];
    stepStates: AgentStepStates;
    currentStep: AgentStepId | undefined;
    id?: string;
}

const AgentStepListImpl: FC<ListProps> = ({ steps, stepStates, currentStep, id }) => {
    const { t } = useTranslation();
    return (
        <div id={id} className="max-h-60 overflow-auto border-b border-separator bg-surface px-4 pb-3 pt-1">
            {AGENT_PHASES.map((phase) => {
                const phaseSteps = phase.steps.filter((s) => steps.includes(s));
                if (phaseSteps.length === 0) return null;
                return (
                    <React.Fragment key={phase.id}>
                        <p className="mb-0.5 mt-2 text-[11px] font-semibold uppercase tracking-wider text-muted">
                            {t(phase.labelId)}
                        </p>
                        <ul aria-label={t(phase.labelId)}>
                            {phaseSteps.map((stepId) => {
                                const { state, result } = stepStates[stepId];
                                const isCurrent = stepId === currentStep;
                                return (
                                    <li
                                        key={stepId}
                                        aria-current={isCurrent ? "step" : undefined}
                                        className={cn(
                                            "flex items-center gap-2 py-0.5 text-sm",
                                            isCurrent && "font-semibold",
                                            state === "pending" && "text-muted",
                                        )}
                                    >
                                        <AgentStateIcon state={state} className="[&_svg]:size-4" />
                                        <span>{t(`app.agent.step.${stepId}`)}</span>
                                        {result || waitWords[state] ? (
                                            <span className="ml-auto text-xs font-normal text-muted">
                                                {result ? reportLineText(t, result) : t(waitWords[state] as MessageId)}
                                            </span>
                                        ) : null}
                                    </li>
                                );
                            })}
                        </ul>
                    </React.Fragment>
                );
            })}
        </div>
    );
};

export const AgentStepList = memo(AgentStepListImpl);

interface ProgressProps {
    steps: AgentStepId[];
    stepStates: AgentStepStates;
    /** 1-based number of the step that runs now, 0 before the first. */
    stepNumber: number;
    finished: boolean;
    /** The header row (title, subtitle, chips) rendered above the pill inside the gradient. */
    children?: ReactNode;
}

const AgentStepProgressImpl: FC<ProgressProps> = ({ steps, stepStates, stepNumber, finished, children }) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const listId = useId();
    const handleToggle = useCallback(() => setExpanded((e) => !e), []);
    const currentStep = finished ? undefined : steps[stepNumber - 1];

    const label = finished
        ? t("app.agent.finished")
        : currentStep
          ? [
                t("app.agent.stepOf", { step: stepNumber, total: steps.length }),
                t(`app.agent.step.${currentStep}`),
                ...(stepStates[currentStep].state === "paused" ? [t("app.agent.stepList.paused")] : []),
                ...(stepStates[currentStep].state === "pausing" ? [t("app.agent.stepList.pausing")] : []),
            ].join(" · ")
          : "";

    return (
        <>
            <div className={ProductColors.AIGradient.className}>
                {children}
                {label ? (
                    <div className="px-4 pb-3">
                        <button
                            type="button"
                            data-testid="agent-step-toggle"
                            aria-expanded={expanded}
                            aria-controls={expanded ? listId : undefined}
                            onClick={handleToggle}
                            className="flex w-full items-center gap-1.5 rounded-lg bg-white/15 px-2.5 py-1.5 text-left text-sm text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                        >
                            <span>{label}</span>
                            <ChevronRight
                                aria-hidden
                                className={cn("ml-auto size-4 transition-transform", expanded && "rotate-90")}
                            />
                        </button>
                        <div aria-hidden data-testid="agent-step-bar" className="mt-2 flex gap-0.5">
                            {steps.map((id) => (
                                <i key={id} className={cn("h-1 flex-1 rounded-sm", barColors[stepStates[id].state])} />
                            ))}
                        </div>
                    </div>
                ) : null}
            </div>
            {expanded ? (
                <AgentStepList id={listId} steps={steps} stepStates={stepStates} currentStep={currentStep} />
            ) : null}
        </>
    );
};

export const AgentStepProgress = memo(AgentStepProgressImpl);
